import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import MainLayout from "../../layouts/MainLayout";
import Tabs from "../../components/Tabs";
import DataTable from "../../components/DataTable";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listExamConfigs, listExamResults, listExamMarksForClass } from "../../api/examApi";
import "../Attendance/Attendance.css";
import "../Dashboard/Dashboard.css";

const academicYearApi = createLookupApi("academic-years");

const URL_TO_TAB = {
  "exam-report-section-failed-list": "section",
  "exam-report-group-failed-list": "group",
  "exam-report-fail-details": "faildetails",
};

const TABS = [
  { key: "section", label: "Section Wise" },
  { key: "group", label: "Group Wise" },
  { key: "faildetails", label: "Fail Details (Section + Group)" },
];

/** Any short-code value < 0 is a sentinel (no mark entered / not configured / absent —
 * see ExamMarkServiceImpl's own -1/-2/-3 convention), never a real score to compare
 * against its pass mark. */
function isRealShortCode(value) {
  return value >= 0;
}

/** Direct port of digicampusbd's ExamReportService#isShortCodeFail — a subject counts
 * as failed if ANY of its configured short codes scored below that short code's own
 * pass mark. */
function isSubjectFailed(mark) {
  for (let i = 1; i <= 8; i++) {
    const value = mark[`shortCode${i}`];
    const passMark = mark[`scPassmark${i}`];
    if (isRealShortCode(value) && value < passMark) {
      return true;
    }
  }
  return false;
}

/**
 * Direct port of digicampusbd's `exam-report-section-failed-list` and
 * `exam-report-group-failed-list` (`ExamReportService#findSectionfailedDetails`/
 * `findGroupfailedDetails`) — for every FAILED student in a section/group, lists
 * which of their subjects they actually failed (with the obtained mark) rather
 * than just a fail-count, using real per-subject short-code data
 * (`GET /api/exam-marks/for-class`, new — reuses `ExamMarkRepository`'s
 * existing native `findForClass` query, only exposing the shape the report
 * needs — see `ExamMarkDetailResponse`). Failed students themselves come
 * from the same `GET /api/exam-results` call the merit/fail-list reports
 * already use, filtered to `resultStatus === "Failed"` and this section/group.
 *
 * Deliberately simplified vs. the old app in one respect: digicampusbd's
 * `SubjectConfiguration` is scoped per *group* with a `subjectMergeId` so two
 * subjects (e.g. a paper 1 + paper 2) can be merged into one graded subject —
 * this app's `ExamSubjectConfiguration` has no group-scoping or merge concept
 * at all (a pre-existing modeling gap from earlier phases, not introduced
 * here), so every `ExamMark` row is already exactly one subject with no
 * merging needed. For institutes not using subject-merging (the common case)
 * this produces the identical result; documented here rather than silently
 * assumed equivalent.
 *
 * Also folds in `exam-report-fail-details` ("Fail List Info",
 * `findSectionfailedDetails`'s dual-scoped sibling —
 * `sectionFailedListWithDetails`, both class+group together): same failed-
 * subject computation, but (1) the displayed total is the sum of only the
 * *failed* subjects' marks (`prepareOnlyFailedSubjectResponse`, not the
 * student's real overall total), (2) each row carries the student's exam
 * remark (`ExamResult.remark`, newly exposed on `ExamResultResponse`), and
 * (3) the same pass/fail summary `ExamGradingPassFailSummaryPage` already
 * computes institute-wide is shown here scoped to just this one class-
 * configuration, matching the old page's own inline summary box.
 */
export default function ExamFailedSubjectDetailsPage() {
  const { pageUrl } = useParams();
  const [tab, setTab] = useState(URL_TO_TAB[pageUrl] || "section");

  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigurationId, setClassConfigurationId] = useState("");
  const [examConfigs, setExamConfigs] = useState([]);
  const [examConfigId, setExamConfigId] = useState("");
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");

  const [results, setResults] = useState([]);
  const [marks, setMarks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [groupId, setGroupId] = useState("");

  useEffect(() => {
    setTab(URL_TO_TAB[pageUrl] || "section");
  }, [pageUrl]);

  useEffect(() => {
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigurationId(String(configs[0].id));
    });
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
  }, []);

  useEffect(() => {
    const cc = classConfigs.find((c) => String(c.id) === classConfigurationId);
    if (!cc) return;
    listExamConfigs(cc.classId).then((configs) => {
      setExamConfigs(configs);
      setExamConfigId(configs.length > 0 ? String(configs[0].examConfigId) : "");
    });
  }, [classConfigurationId, classConfigs]);

  useEffect(() => {
    if (!examConfigId || !academicYear) {
      setResults([]);
      setMarks([]);
      return;
    }
    setLoading(true);
    setError("");
    Promise.all([
      listExamResults(examConfigId),
      classConfigurationId ? listExamMarksForClass(examConfigId, classConfigurationId, academicYear) : Promise.resolve([]),
    ])
      .then(([r, m]) => {
        setResults(r);
        setMarks(m);
      })
      .catch(() => setError("Failed to load exam data."))
      .finally(() => setLoading(false));
  }, [examConfigId, classConfigurationId, academicYear]);

  const failedInScope = useMemo(() => {
    return results.filter((r) => {
      if (r.resultStatus !== "Failed" || r.academicYear !== academicYear) return false;
      if (String(r.classConfigurationId) !== classConfigurationId) return false;
      if (tab === "group" || tab === "faildetails") return String(r.groupId) === groupId;
      return true;
    });
  }, [results, academicYear, classConfigurationId, tab, groupId]);

  // Direct port of ExamReportService#findSectionWisePassFailSummaryList — shown
  // once above the "Fail Details" table, matching the old page's own inline
  // summary (this app already has the fuller institute-wide version of this
  // exact computation on ExamGradingPassFailSummaryPage; this is the same
  // arithmetic scoped down to just the one selected class-configuration).
  const scopeSummary = useMemo(() => {
    const scoped = results.filter((r) => String(r.classConfigurationId) === classConfigurationId && r.academicYear === academicYear);
    const participatedInExam = scoped.length;
    const totalPass = scoped.filter((r) => r.resultStatus === "Passed").length;
    const totalFail = participatedInExam - totalPass;
    return {
      participatedInExam,
      totalPass,
      totalFail,
      passPercentage: participatedInExam > 0 ? Math.round((totalPass * 10000) / participatedInExam) / 100 : 0,
      failPercentage: participatedInExam > 0 ? Math.round((totalFail * 10000) / participatedInExam) / 100 : 0,
    };
  }, [results, classConfigurationId, academicYear]);

  const groups = useMemo(() => {
    const seen = new Map();
    results
      .filter((r) => String(r.classConfigurationId) === classConfigurationId)
      .forEach((r) => {
        if (r.groupId != null && !seen.has(r.groupId)) seen.set(r.groupId, { id: r.groupId, name: r.groupName });
      });
    return Array.from(seen.values());
  }, [results, classConfigurationId]);

  // Re-validate (not just "is something already selected") whenever the
  // class changes the available groups — see ExamMeritReportsPage/
  // ExamForthSubjectAssignPage for the live bug this guards against (a
  // stale group id from a previous class silently matching zero students
  // in the new one instead of falling back to a real option).
  useEffect(() => {
    if (groups.length === 0) return;
    if (!groups.some((g) => String(g.id) === groupId)) setGroupId(String(groups[0].id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  const marksByStudent = useMemo(() => {
    const map = new Map();
    marks.forEach((m) => {
      if (!map.has(m.identificationId)) map.set(m.identificationId, []);
      map.get(m.identificationId).push(m);
    });
    return map;
  }, [marks]);

  const rows = failedInScope.map((student) => {
    const studentMarks = marksByStudent.get(student.identificationId) || [];
    const failedSubjects = studentMarks.filter(isSubjectFailed);
    const subjectNames = failedSubjects.map((m) => `${m.subjectName} (${m.totalScore})`).join(", ");
    // "Fail Details" totals only the failed subjects' own marks (ExamReportService's
    // own `prepareOnlyFailedSubjectResponse`); the other 2 tabs show the student's
    // real overall total mark instead (`prepareStudentFailedSubjectResponse`).
    const failedSubjectsTotal = failedSubjects.reduce((sum, m) => sum + m.totalScore, 0);
    return {
      ...student,
      noOfFailedSubjects: failedSubjects.length,
      subjectNames: subjectNames || "—",
      failedSubjectsTotal,
    };
  });

  const columns = [
    { key: "customStudentId", label: "Student ID" },
    { key: "studentName", label: "Name" },
    { key: "studentRoll", label: "Roll" },
    { key: "sectionName", label: "Section" },
    ...(tab === "faildetails"
      ? [{ key: "failedSubjectsTotal", label: "Total Marks (Failed Subjects Only)", render: (row) => row.failedSubjectsTotal.toFixed(2) }]
      : [{ key: "totalMark", label: "Total Marks", render: (row) => Number(row.totalMark).toFixed(2) }]),
    { key: "noOfFailedSubjects", label: "Number of Failed Subjects" },
    { key: "subjectNames", label: "Failed Subjects" },
    ...(tab === "faildetails" ? [{ key: "remark", label: "Remarks", render: (row) => row.remark || "—" }] : []),
  ];

  return (
    <MainLayout>
      <div className="page-header">
        <h1>
          {tab === "section" && "Section Wise Failed List"}
          {tab === "group" && "Group Wise Failed List"}
          {tab === "faildetails" && "Fail List Info"}
        </h1>
      </div>

      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}

      <div className="attendance-filters">
        <label>
          Class
          <select value={classConfigurationId} onChange={(e) => setClassConfigurationId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Exam
          <select value={examConfigId} onChange={(e) => setExamConfigId(e.target.value)}>
            {examConfigs.length === 0 && <option value="">No exams configured</option>}
            {examConfigs.map((ec) => (
              <option key={ec.examConfigId} value={ec.examConfigId}>
                {ec.examName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        {(tab === "group" || tab === "faildetails") && (
          <label>
            Group
            <select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {tab === "faildetails" && !loading && (
        <div className="dashboard-cards" style={{ marginBottom: "1rem" }}>
          <div className="dashboard-card">
            <div className="value">{scopeSummary.participatedInExam}</div>
            <div className="label">Participated</div>
          </div>
          <div className="dashboard-card">
            <div className="value">{scopeSummary.totalPass}</div>
            <div className="label">Pass ({scopeSummary.passPercentage}%)</div>
          </div>
          <div className="dashboard-card">
            <div className="value">{scopeSummary.totalFail}</div>
            <div className="label">Fail ({scopeSummary.failPercentage}%)</div>
          </div>
        </div>
      )}

      {loading ? <p>Loading...</p> : <DataTable columns={columns} rows={rows} />}
      {!loading && rows.length === 0 && <p>No failed students found for this selection.</p>}
    </MainLayout>
  );
}
